import { Component, ElementRef, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { faSignOutAlt, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../shared/services/auth.service';
import { LoggerService } from '../../shared/services/logger.service';
import { NotifyService } from '../../shared/services/notify.service';
import { RouterLink } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

/** Error when invalid control is dirty, touched, or submitted. */
export class Ap1ErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | null): boolean {
    const isSubmitted = form && form.submitted;

    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [MatButtonModule, RouterLink, MatFormFieldModule, MatIconModule, ReactiveFormsModule, MatInputModule],
})
export class LoginComponent {
  public acao: string;
  public faUser = faUser;
  public faUsers = faUsers;
  public faSignOutAlt = faSignOutAlt;
  public matcher = new Ap1ErrorStateMatcher();
  private greetingsShow = false;
  public wrongLogin: string = undefined;
  public loading = false;
  public errorMessage = '';
  public hide = true;

  @ViewChild('loginModal') loginModal: ElementRef;
  @ViewChild('registerModal') registerModal: ElementRef;
  @ViewChild('recoverModal') recoverModal: ElementRef;

  public loginFormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, Validators.minLength(6), Validators.maxLength(20)]),
  });

  constructor(
    public authService: AuthService,
    private modalService: NgbModal,
    private notifySvc: NotifyService,
    private logger: LoggerService
  ) {}

  get email() {
    return this.loginFormGroup.get('email');
  }

  get password() {
    return this.loginFormGroup.get('password');
  }

  public loginWithEmail(): void {
    this.loading = true;
    this.authService
      .doLogin(this.email.value, this.password.value)
      .then(() => {
        this.logger.debug('Login:loginWithEmail:Ok');
        this.greetingsShow = true;
        this.dismissModal();
        this.loginFormGroup.reset();
      })
      .catch(err => {
        this.logger.warning('Login:LoginEmail:' + err.code);
        let mensagem = 'Erro desconhecido. Tente mais tarde';
        if (err.code === 'auth/wrong-password') {
          mensagem = 'Senha incorreta';
        } else if (err.code === 'auth/user-disabled') {
          mensagem = 'Usuário está desabilitado';
        } else if (err.code === 'auth/user-not-found') {
          mensagem = 'Usuário não encontrado';
        } else if (err.code === 'auth/too-many-requests') {
          mensagem = 'Muitas tentativas realizadas. Aguarde algumas horas para tentar novamente';
        }
        this.wrongLogin = mensagem;
        this.loading = false;
        this.loginFormGroup.markAsPristine();
        this.notifySvc.showWarning(mensagem);
      });
  }

  public loginWithGoogle(): void {
    this.loading = true;
    this.authService
      .doGoogleLogin()
      .then(() => {
        this.logger.debug('Login:loginWithGoogle:Ok');
        this.dismissModal();
      })
      .catch(err => {
        this.logger.warning('Header:LoginGoogle:' + err.code);
        let mensagem = 'Falha no login - Erro desconhecido';
        if (err.code === 'auth/account-exists-with-different-credential') {
          mensagem = 'Usuário já está cadastrado com outra forma de acesso';
        } else if (err.code === 'auth/cancelled-popup-request') {
          mensagem = 'Janela já aberta com outra autenticação';
        } else if (err.code === 'auth/popup-closed-by-user') {
          mensagem = 'Processo de login cancelado';
        } else if (err.code === 'auth/popup-blocked') {
          mensagem = 'A abertura da janela foi bloqueada pelo navegador. Permitir pop-up';
        }
        this.dismissModal();
        this.notifySvc.showWarning(mensagem);
      });
  }

  public loginWithFacebook(): void {
    this.loading = true;
    this.authService
      .doFacebookLogin()
      .then(() => {
        this.logger.debug('Login:loginWithFacebook:Ok');
        this.dismissModal();
      })
      .catch(err => {
        this.logger.warning('Header:LoginFacebook:' + err.code);
        let mensagem = 'Falha no login - Erro desconhecido';
        if (err.code === 'auth/account-exists-with-different-credential') {
          mensagem = 'Usuário já está cadastrado com outra forma de acesso';
        } else if (err.code === 'auth/cancelled-popup-request') {
          mensagem = 'Janela já aberta com outra autenticação';
        } else if (err.code === 'auth/popup-closed-by-user') {
          mensagem = 'Processo de login cancelado';
        } else if (err.code === 'auth/popup-blocked') {
          mensagem = 'A abertura da janela foi bloqueada pelo navegador. Permitir pop-up';
        }
        this.dismissModal();
        this.notifySvc.showWarning(mensagem);
      });
  }

  public registerWithEmail(): void {
    this.loading = true;
    if (!this.loginFormGroup.invalid) {
      this.authService
        .doRegister(this.email.value, this.password.value)
        .then(() => {
          this.logger.debug('Login:registerWithEmail:Ok');
          this.dismissModal();
          this.notifySvc.showInfo('Usuário cadastrado com sucesso');
        })
        .catch(err => {
          this.logger.warning({ 'Login:RegisterEmail': err });
          let mensagem = 'Falha no login - Erro desconhecido';
          if (err.code === 'auth/email-already-in-use') {
            mensagem = 'Este e-mail já está cadastrado';
          } else if (err.code === 'auth/invalid-email') {
            mensagem = 'E-mail inválido. Corrija e tente novamente';
          } else if (err.code === 'auth/operation-not-allowed') {
            mensagem = 'Operação não permitida';
          } else if (err.code === 'auth/weak-password') {
            mensagem = 'A senha é fraca. Aumente a complexidade';
          }
          this.dismissModal();
          this.notifySvc.showWarning(mensagem);
        });
    }
  }

  public recoverPassword(): void {
    this.loading = true;
    this.authService
      .sendPasswordResetEmail(this.email.value)
      .then(() => {
        this.logger.debug('Login:recoverPassword:Ok');
        this.dismissModal();
        this.notifySvc.showInfo('Um email será enviado para recuperar a senha caso exista alguma conta cadastrada');
      })
      .catch(err => {
        this.logger.warning('Login:RecoverPassword:' + err);
        let mensagem = 'Falha na recuperação da senha';
        if (err.code === 'auth/invalid-email') {
          mensagem = 'E-mail inválido. Corrija e tente novamente';
        } else if (err.code === 'auth/user-not-found') {
          mensagem = 'E-mail não encontrado';
        }
        this.notifySvc.showWarning(mensagem);
      });
  }

  public dismissModal(): void {
    this.modalService.dismissAll();
    this.loading = false;
  }

  public open(content: ElementRef | TemplateRef<any>) {
    this.dismissModal();
    if (this.email.value && this.email.value.length == 0 && this.password.value.length == 0) {
      this.loginFormGroup.reset();
    }
    this.modalService.open(content, { ariaLabelledBy: 'modal-dialog' }).result.then(
      result => {
        this.logger.debug('Login:DialogClosed:' + result);
      },
      () => {
        this.logger.debug('Login:DialogDismissed');
        this.loading = false;
      }
    );
  }

  public openLogin() {
    this.open(this.loginModal);
  }

  public openRegister() {
    this.open(this.registerModal);
  }

  public openRecover() {
    this.open(this.recoverModal);
  }

  closeError() {
    this.errorMessage = '';
  }

  getEmailError() {
    if (this.email.hasError('required')) {
      return 'O email é obrigatório';
    }
    if (this.email.hasError('email')) {
      return 'Email inválido';
    }
  }

  getPasswordError() {
    if (this.password.hasError('required')) {
      return 'A senha é obrigatória';
    }
    if (this.password.hasError('minlength')) {
      return 'A senha deve possuir pelo menos 6 caracteres';
    }
    if (this.password.hasError('maxlength')) {
      return 'A senha deve possuir no máximo 20 caracteres';
    }
  }
}
